import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  MessageSquare, Smartphone, Eye, RotateCcw, Save, Info, Send, Users, ShieldAlert,
  Wrench, CheckCircle2, XCircle, MinusCircle, Loader2, Filter, History,
} from "lucide-react";
import { toast } from "sonner";

const AUDIENCE_LABEL = {
  customer: "Sent to the customer",
  technician: "Sent to the technician",
  admin: "Sent to all admins",
};

const TEMPLATE_GROUPS = [
  {
    id: "service",
    title: "Automated Service Messages",
    subtitle: "Fired automatically as service jobs move through the pipeline and for customer follow-ups.",
    icon: Wrench,
    color: "#166534",
    match: (k) => k.startsWith("service.") || k.startsWith("followup."),
  },
  {
    id: "security",
    title: "Security Alerts",
    subtitle: "Sent to admins when the system takes protective action (e.g. brute-force auto-lock).",
    icon: ShieldAlert,
    color: "#991b1b",
    match: (k) => k.startsWith("security."),
  },
];

export default function NotificationTemplates() {
  const { user } = useAuth();
  const [tab, setTab] = useState("automated");

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="p-8 lg:p-10 max-w-[1200px]">
      <PageHeader
        title="Messages"
        subtitle="Customize automated notifications, run one-off broadcasts to filtered customers, and review past sends."
        testid="page-notification-templates"
      />

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-6" data-testid="messages-tabs">
          <TabsTrigger value="automated" data-testid="tab-automated"><MessageSquare size={14} className="mr-2" /> Automated Templates</TabsTrigger>
          <TabsTrigger value="broadcast" data-testid="tab-broadcast"><Send size={14} className="mr-2" /> Broadcast</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><History size={14} className="mr-2" /> History</TabsTrigger>
        </TabsList>

        <TabsContent value="automated"><AutomatedTemplates /></TabsContent>
        <TabsContent value="broadcast"><BroadcastComposer onSent={() => setTab("history")} /></TabsContent>
        <TabsContent value="history"><BroadcastHistory /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Templates Tab ----------
function AutomatedTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notification-templates");
      setTemplates(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load templates");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    return TEMPLATE_GROUPS.map((g) => ({ ...g, items: templates.filter((t) => g.match(t.key)) })).filter((g) => g.items.length);
  }, [templates]);

  if (loading) return <div className="text-center py-16 text-[#8A8F98]">Loading templates…</div>;

  return (
    <>
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-md px-4 py-3 mb-6 text-sm text-yellow-200 flex gap-2">
        <Info size={16} className="mt-0.5 shrink-0" />
        <div>
          Turn each channel off if you don&apos;t want the message to go out. Keep the placeholders (e.g. <code className="bg-[#121815] px-1 rounded font-mono text-xs">{"{name}"}</code>) so the message is personalized.
        </div>
      </div>

      {grouped.map((g) => {
        const Icon = g.icon;
        return (
          <section key={g.id} className="mb-8" data-testid={`tpl-group-${g.id}`}>
            <header className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-md flex items-center justify-center text-white" style={{ background: g.color }}>
                <Icon size={16} />
              </div>
              <div>
                <div className="font-heading font-bold text-[#F8F9FA]">{g.title}</div>
                <div className="text-xs text-[#8A8F98]">{g.subtitle}</div>
              </div>
            </header>
            <div className="grid grid-cols-1 gap-5">
              {g.items.map((t) => <TemplateCard key={t.key} tpl={t} onSaved={load} />)}
            </div>
          </section>
        );
      })}
    </>
  );
}

function TemplateCard({ tpl, onSaved }) {
  const [body, setBody] = useState(tpl.body);
  const [sms, setSms] = useState(tpl.sms_enabled);
  const [wa, setWa] = useState(tpl.whatsapp_enabled);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setBody(tpl.body); setSms(tpl.sms_enabled); setWa(tpl.whatsapp_enabled); }, [tpl]);

  const dirty = body !== tpl.body || sms !== tpl.sms_enabled || wa !== tpl.whatsapp_enabled;

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/notification-templates/${tpl.key}`, { body, sms_enabled: sms, whatsapp_enabled: wa });
      toast.success(`${tpl.label} saved`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to save"); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try { await api.post(`/notification-templates/${tpl.key}/reset`); toast.success(`Reset to default`); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const doPreview = async () => {
    try { const { data } = await api.post(`/notification-templates/${tpl.key}/preview`, { body }); setPreview(data.body); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Preview failed"); }
  };

  const insertVar = (v) => setBody((prev) => `${prev}{${v}}`);

  return (
    <section className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm overflow-hidden" data-testid={`tpl-card-${tpl.key}`}>
      <header className="px-5 py-4 border-b border-[#3A4640] flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[250px]">
          <div className="font-heading font-bold text-[#F8F9FA] flex items-center gap-2">
            {tpl.label}
            {tpl.is_customized && (<span className="text-[10px] uppercase tracking-wider bg-blue-100 text-blue-300 px-1.5 py-0.5 rounded font-heading">Customized</span>)}
          </div>
          <div className="text-xs text-[#8A8F98] mt-0.5">{tpl.description}</div>
          <div className="text-xs text-[#8A8F98] mt-1">{AUDIENCE_LABEL[tpl.audience] || tpl.audience}</div>
        </div>
        <div className="flex gap-4 items-center text-xs">
          <label className="flex items-center gap-2" data-testid={`tpl-sms-toggle-${tpl.key}`}>
            <Smartphone size={14} className="text-[#8A8F98]" /><span className="font-heading">SMS</span>
            <Switch checked={sms} onCheckedChange={setSms} />
          </label>
          <label className="flex items-center gap-2" data-testid={`tpl-whatsapp-toggle-${tpl.key}`}>
            <MessageSquare size={14} className="text-[#39FF14]" /><span className="font-heading">WhatsApp</span>
            <Switch checked={wa} onCheckedChange={setWa} />
          </label>
        </div>
      </header>
      <div className="px-5 py-4">
        <Label className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading">Message body</Label>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} className="font-mono text-sm" data-testid={`tpl-body-${tpl.key}`} />
        <div className="mt-2 flex flex-wrap gap-1 items-center">
          <span className="text-xs text-[#8A8F98] mr-1">Insert:</span>
          {(tpl.variables || []).map((v) => (
            <button key={v} type="button" onClick={() => insertVar(v)} className="text-xs bg-[#121815] hover:bg-[#1A1F1C] text-[#F8F9FA] rounded px-2 py-0.5 font-mono" data-testid={`tpl-var-${tpl.key}-${v}`}>
              {"{" + v + "}"}
            </button>
          ))}
        </div>
        {preview && (
          <div className="mt-4 border border-[#39FF14]/25 bg-green-50 rounded p-3" data-testid={`tpl-preview-${tpl.key}`}>
            <div className="text-xs uppercase tracking-wider text-[#39FF14] font-heading font-semibold mb-1">Preview (with sample data)</div>
            <div className="text-sm text-[#F8F9FA] whitespace-pre-wrap">{preview}</div>
          </div>
        )}
        <div className="mt-4 flex flex-wrap gap-2 items-center">
          <Button variant="outline" onClick={doPreview} disabled={!body.trim()} data-testid={`tpl-preview-btn-${tpl.key}`}>
            <Eye size={14} className="mr-1" /> Preview
          </Button>
          <Button onClick={save} disabled={!dirty || busy || !body.trim()} className="bg-[#39FF14] hover:bg-[#2ECC71]" data-testid={`tpl-save-${tpl.key}`}>
            <Save size={14} className="mr-1" /> Save
          </Button>
          {tpl.is_customized && (
            <Button variant="ghost" onClick={reset} disabled={busy} className="text-red-600 hover:bg-red-500/10 hover:text-red-300" data-testid={`tpl-reset-${tpl.key}`}>
              <RotateCcw size={14} className="mr-1" /> Reset to default
            </Button>
          )}
          {tpl.updated_at && (
            <span className="text-xs text-[#8A8F98] ml-auto">Last edited {formatDate(tpl.updated_at)} by {tpl.updated_by || "-"}</span>
          )}
        </div>
      </div>
    </section>
  );
}

// ---------- Broadcast Composer ----------
const EMPTY_FILTERS = { village: "", brand: "", category: "", has_purchases: "any", custom_key: "", custom_value: "" };

function toBackendFilters(f) {
  return {
    village: f.village || null,
    brand: f.brand || null,
    category: f.category || null,
    has_purchases: f.has_purchases === "any" ? null : f.has_purchases === "yes",
    custom_key: f.custom_key || null,
    custom_value: f.custom_value || null,
  };
}

function BroadcastComposer({ onSent }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [name, setName] = useState("");
  const [body, setBody] = useState("Hi {name}, this is a message from {business}.");
  const [sms, setSms] = useState(true);
  const [wa, setWa] = useState(true);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewSample, setPreviewSample] = useState([]);
  const [optedOutExcluded, setOptedOutExcluded] = useState(0);
  const [previewing, setPreviewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const setF = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/broadcasts/preview", { filters: toBackendFilters(filters) });
      setPreviewCount(data.count);
      setPreviewSample(data.sample || []);
      setOptedOutExcluded(data.opted_out_excluded || 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
    } finally { setPreviewing(false); }
  };

  const openConfirm = () => {
    if (!body.trim()) return toast.error("Message body is required");
    if (!sms && !wa) return toast.error("Enable SMS or WhatsApp");
    if (previewCount === null) return toast.error("Please preview the recipient list first");
    if (previewCount === 0) return toast.error("No customers match the current filters");
    setConfirmOpen(true);
  };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/broadcasts", {
        name: name || "Untitled broadcast",
        body,
        sms_enabled: sms,
        whatsapp_enabled: wa,
        filters: toBackendFilters(filters),
      });
      toast.success(`Broadcast sent — ${data.sent} delivered, ${data.failed} failed, ${data.skipped} skipped`);
      setConfirmOpen(false);
      // Soft-reset
      setName(""); setBody("Hi {name}, this is a message from {business}.");
      setPreviewCount(null); setPreviewSample([]); setOptedOutExcluded(0);
      onSent?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Broadcast failed");
    } finally { setSending(false); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <div className="space-y-5">
        <section className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={16} className="text-[#39FF14]" />
            <h3 className="font-heading font-bold text-[#F8F9FA]">Who should receive this?</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading">Village contains</Label>
              <Input data-testid="bc-village" value={filters.village} onChange={(e) => setF("village", e.target.value)} placeholder="e.g. Kolar" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading">Purchase brand</Label>
              <Input data-testid="bc-brand" value={filters.brand} onChange={(e) => setF("brand", e.target.value)} placeholder="e.g. Mahindra" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading">Purchase category</Label>
              <Input data-testid="bc-category" value={filters.category} onChange={(e) => setF("category", e.target.value)} placeholder="e.g. Tractor" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading">Purchase status</Label>
              <Select value={filters.has_purchases} onValueChange={(v) => setF("has_purchases", v)}>
                <SelectTrigger data-testid="bc-has-purchases"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any customer</SelectItem>
                  <SelectItem value="yes">Only customers with purchases</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading">Custom field key</Label>
              <Input data-testid="bc-custom-key" value={filters.custom_key} onChange={(e) => setF("custom_key", e.target.value)} placeholder="e.g. crop_type" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading">Custom field value</Label>
              <Input data-testid="bc-custom-value" value={filters.custom_value} onChange={(e) => setF("custom_value", e.target.value)} placeholder="e.g. sugarcane" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button variant="outline" onClick={runPreview} disabled={previewing} data-testid="bc-preview-btn">
              {previewing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Users size={14} className="mr-2" />}
              Preview recipients
            </Button>
            {previewCount !== null && (
              <span className="text-sm text-[#F8F9FA]" data-testid="bc-preview-count">
                <b className="text-[#39FF14]">{previewCount}</b> customer{previewCount === 1 ? "" : "s"} match{previewCount === 1 ? "es" : ""}
                {optedOutExcluded > 0 && (
                  <span className="text-yellow-400 ml-2" data-testid="bc-opted-out-count">
                    · {optedOutExcluded} opted-out excluded
                  </span>
                )}
                {previewSample.length > 0 && <span className="text-[#8A8F98]"> — {previewSample.map((s) => s.name).join(", ")}{previewCount > previewSample.length ? "…" : ""}</span>}
              </span>
            )}
          </div>
        </section>

        <section className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Send size={16} className="text-yellow-400" />
            <h3 className="font-heading font-bold text-[#F8F9FA]">Compose your message</h3>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading">Broadcast name (internal)</Label>
              <Input data-testid="bc-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Monsoon service reminder" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading">Message body</Label>
              <Textarea data-testid="bc-body" value={body} onChange={(e) => setBody(e.target.value)} rows={5} className="font-mono text-sm" />
              <div className="mt-1 text-xs text-[#8A8F98]">
                Placeholders: <code className="font-mono">{"{name}"}</code>, <code className="font-mono">{"{business}"}</code>, <code className="font-mono">{"{phone}"}</code>
              </div>
            </div>
            <div className="flex flex-wrap gap-5 items-center text-sm">
              <label className="flex items-center gap-2" data-testid="bc-sms-toggle">
                <Smartphone size={14} className="text-[#8A8F98]" /><span className="font-heading">SMS</span>
                <Switch checked={sms} onCheckedChange={setSms} />
              </label>
              <label className="flex items-center gap-2" data-testid="bc-whatsapp-toggle">
                <MessageSquare size={14} className="text-[#39FF14]" /><span className="font-heading">WhatsApp</span>
                <Switch checked={wa} onCheckedChange={setWa} />
              </label>
            </div>
          </div>
        </section>
      </div>

      <aside className="lg:sticky lg:top-6">
        <div className="bg-[#1A1F1C] text-white rounded-md p-5 space-y-3">
          <div className="text-xs uppercase tracking-wider text-yellow-400 font-heading">Ready to send</div>
          <div className="text-3xl font-heading font-extrabold" data-testid="bc-ready-count">{previewCount ?? "—"}</div>
          <div className="text-xs text-[#8A8F98]">customers matched</div>
          <div className="text-xs text-[#8A8F98]">Channels: {sms && "SMS"}{sms && wa && " + "}{wa && "WhatsApp"}{!sms && !wa && "None"}</div>
          <Button
            onClick={openConfirm}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-[#0B0F0C]"
            data-testid="bc-send-btn"
          >
            <Send size={14} className="mr-2" /> Send broadcast
          </Button>
          <div className="text-[11px] text-[#8A8F98]">Every send is logged on the customer&apos;s profile.</div>
        </div>
      </aside>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Send broadcast to {previewCount} customers?</DialogTitle>
            <DialogDescription>
              This will dispatch {sms && "SMS"}{sms && wa && " and "}{wa && "WhatsApp"} messages via Twilio. The action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs bg-[#0B0F0C] border border-[#3A4640] rounded p-3 whitespace-pre-wrap font-mono">{body}</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sending}>Cancel</Button>
            <Button onClick={send} disabled={sending} className="bg-yellow-500 hover:bg-yellow-600 text-[#0B0F0C]" data-testid="bc-confirm-send">
              {sending ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
              Yes, send now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- History Tab ----------
function BroadcastHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/broadcasts"); setRows(data || []); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load history"); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-center py-16 text-[#8A8F98]">Loading history…</div>;
  if (rows.length === 0) return <div className="text-center py-16 text-[#8A8F98]" data-testid="bc-history-empty">No broadcasts yet. Compose your first one in the Broadcast tab.</div>;

  return (
    <section className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#0B0F0C] border-b border-[#3A4640] text-xs uppercase tracking-wider text-[#8A8F98] font-heading">
          <tr>
            <th className="text-left px-4 py-3">Sent</th>
            <th className="text-left px-4 py-3">Name</th>
            <th className="text-left px-4 py-3">Recipients</th>
            <th className="text-left px-4 py-3">Channels</th>
            <th className="text-left px-4 py-3">Delivery</th>
            <th className="text-left px-4 py-3">Sent by</th>
          </tr>
        </thead>
        <tbody data-testid="bc-history-tbody">
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-[#3A4640] last:border-0 hover:bg-[#0B0F0C]" data-testid={`bc-row-${r.id}`}>
              <td className="px-4 py-3 text-[#F8F9FA]">{formatDate(r.sent_at)}</td>
              <td className="px-4 py-3">
                <div className="font-medium text-[#F8F9FA]">{r.name}</div>
                <div className="text-xs text-[#8A8F98] line-clamp-1 max-w-[360px]">{r.body}</div>
              </td>
              <td className="px-4 py-3 font-mono text-xs">{r.recipient_count}</td>
              <td className="px-4 py-3 text-xs">
                {r.sms_enabled && <span className="inline-block bg-[#121815] px-1.5 py-0.5 rounded mr-1">SMS</span>}
                {r.whatsapp_enabled && <span className="inline-block bg-green-50 text-[#39FF14] px-1.5 py-0.5 rounded">WhatsApp</span>}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-3 text-xs font-mono">
                  <span className="inline-flex items-center gap-1 text-[#39FF14]"><CheckCircle2 size={12} /> {r.sent}</span>
                  <span className="inline-flex items-center gap-1 text-red-600"><XCircle size={12} /> {r.failed}</span>
                  <span className="inline-flex items-center gap-1 text-[#8A8F98]"><MinusCircle size={12} /> {r.skipped}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-xs text-[#8A8F98]">{r.sent_by_name || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
